import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Menu,
  Select,
  Checkbox,
} from '@mui/material';
import { ArrowLeft, Search, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, MoreVertical } from 'lucide-react';
import { appointmentFlowApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';

const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const emptyForm = {
  name: '',
  status: 'active',
  workingDays: [1, 2, 3, 4, 5],
  openTime: '',
  closeTime: '',
  timezone: '',
};

const statusLabel = (value) => (value === 1 || value === 'active' ? 'active' : 'inactive');

const BranchesPage = () => {
  const { showError } = useUIStore();
  const { confirm } = useConfirm();
  const [branches, setBranches] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionBranch, setActionBranch] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchServices, setBranchServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({
    serviceId: '',
  });
  const [serviceSelection, setServiceSelection] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [serviceEmployees, setServiceEmployees] = useState([]);
  const [employeeSelection, setEmployeeSelection] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const rowsPerPage = 10;
  const isActionMenuOpen = Boolean(actionAnchorEl);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, branches]);

  const fetchBranches = async () => {
    try {
      const response = await appointmentFlowApi.getBranches();
      const data = response?.data?.data || response?.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      setBranches([]);
    }
  };

  const applyFilters = () => {
    if (!searchTerm) {
      setFilteredData(branches);
      setCurrentPage(1);
      return;
    }

    const search = searchTerm.toLowerCase();
    const filtered = branches.filter((item) => (item.name || '').toLowerCase().includes(search));
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWorkingDayToggle = (day) => {
    setFormData((prev) => {
      const exists = prev.workingDays.includes(day);
      return {
        ...prev,
        workingDays: exists
          ? prev.workingDays.filter((d) => d !== day)
          : [...prev.workingDays, day].sort(),
      };
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.createBranch({
        name: formData.name,
        status: formData.status,
        workingDays: formData.workingDays,
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        timezone: formData.timezone || null,
      });
      setShowAddModal(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add branch');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.updateBranch(formData.id, {
        name: formData.name,
        status: formData.status,
        workingDays: formData.workingDays,
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        timezone: formData.timezone || null,
      });
      setShowEditModal(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update branch');
    }
  };

  const handleEdit = (branch) => {
    setFormData({
      id: branch.id,
      name: branch.name || '',
      status: statusLabel(branch.status),
      workingDays: Array.isArray(branch.workingDays) ? branch.workingDays : [],
      openTime: branch.openTime || '',
      closeTime: branch.closeTime || '',
      timezone: branch.timezone || '',
    });
    setShowEditModal(true);
  };

  const handleDelete = async (branchId) => {
    const isConfirmed = await confirm({
      title: 'Delete Branch',
      message: 'Are you sure you want to delete this branch?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await appointmentFlowApi.deleteBranch(branchId);
      fetchBranches();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to delete branch');
    }
  };

  const handleStatusToggle = async (branchId, currentStatus) => {
    const nextStatus = statusLabel(currentStatus) === 'active' ? 'inactive' : 'active';
    try {
      await appointmentFlowApi.updateBranch(branchId, { status: nextStatus });
      fetchBranches();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleOpenServices = async (branch) => {
    setSelectedBranch(branch);
    setShowServicesModal(true);
    setBranchServices([]);
    setAllServices([]);
    setServiceSelection([]);
    setServiceSearch('');
    setSelectedServiceId('');
    setServiceEmployees([]);
    setEmployeeSelection([]);
    setEmployeeSearch('');
    setAllEmployees([]);
    try {
      const [mappedResponse, allResponse] = await Promise.all([
        appointmentFlowApi.getServices({ branchId: branch.id }),
        appointmentFlowApi.getServices(),
      ]);
      const mapped = mappedResponse?.data?.data || mappedResponse?.data || [];
      const all = allResponse?.data?.data || allResponse?.data || [];
      setBranchServices(Array.isArray(mapped) ? mapped : []);
      setAllServices(Array.isArray(all) ? all : []);
    } catch (error) {
      setBranchServices([]);
      showError(error?.response?.data?.message || 'Failed to load services');
    }
  };

  const handleOpenEmployees = async (branch) => {
    setSelectedBranch(branch);
    setShowEmployeesModal(true);
    setBranchServices([]);
    setSelectedServiceId('');
    setServiceEmployees([]);
    setEmployeeSelection([]);
    setEmployeeSearch('');
    setAllEmployees([]);
    try {
      const response = await appointmentFlowApi.getServices({ branchId: branch.id });
      const data = response?.data?.data || response?.data || [];
      setBranchServices(Array.isArray(data) ? data : []);
      await loadAllEmployees();
    } catch (error) {
      setBranchServices([]);
      showError(error?.response?.data?.message || 'Failed to load services');
    }
  };

  const loadServiceEmployees = async (serviceId) => {
    if (!serviceId) {
      setServiceEmployees([]);
      return;
    }
    try {
      const response = await appointmentFlowApi.getServiceEmployees({ serviceId });
      const data = response?.data?.data || response?.data || [];
      setServiceEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      setServiceEmployees([]);
      showError(error?.response?.data?.message || 'Failed to load employees');
    }
  };

  const loadAllEmployees = async () => {
    try {
      const response = await appointmentFlowApi.getEmployees();
      const data = response?.data?.data || response?.data || [];
      setAllEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      setAllEmployees([]);
    }
  };

  const mappedServiceIds = useMemo(
    () => branchServices.map((service) => service.id),
    [branchServices]
  );

  const selectableServices = useMemo(
    () => allServices.filter((service) => !mappedServiceIds.includes(service.id)),
    [allServices, mappedServiceIds]
  );

  const selectableEmployees = useMemo(() => {
    const mappedEmployeeIds = new Set(serviceEmployees.map((employee) => employee.id));
    return allEmployees.filter((employee) => !mappedEmployeeIds.has(employee.id));
  }, [allEmployees, serviceEmployees]);

  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!selectedBranch?.id) return;
    try {
      if (!serviceForm.serviceId) {
        showError('Select a service');
        return;
      }
      await appointmentFlowApi.addBranchService({
        branchId: selectedBranch.id,
        serviceId: serviceForm.serviceId,
      });
      setServiceForm({ serviceId: '' });
      const response = await appointmentFlowApi.getServices({ branchId: selectedBranch.id });
      const data = response?.data?.data || response?.data || [];
      setBranchServices(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add service');
    }
  };

  const handleAddSelectedServices = async () => {
    if (!selectedBranch?.id) return;
    if (!serviceSelection.length) {
      showError('Select services to add');
      return;
    }
    try {
      await Promise.all(
        serviceSelection.map((serviceId) =>
          appointmentFlowApi.addBranchService({
            branchId: selectedBranch.id,
            serviceId,
          })
        )
      );
      setServiceSelection([]);
      const response = await appointmentFlowApi.getServices({ branchId: selectedBranch.id });
      const data = response?.data?.data || response?.data || [];
      setBranchServices(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add services');
    }
  };

  const handleRemoveService = async (serviceId) => {
    const isConfirmed = await confirm({
      title: 'Remove Service',
      message: 'Remove this service?',
      confirmText: 'Remove',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!isConfirmed) return;
    try {
      await appointmentFlowApi.removeBranchService({
        branchId: selectedBranch.id,
        serviceId,
      });
      setBranchServices((prev) => prev.filter((service) => service.id !== serviceId));
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to remove service');
    }
  };

  const handleAddEmployees = async () => {
    if (!selectedServiceId) {
      showError('Select a service');
      return;
    }
    if (!employeeSelection.length) {
      showError('Select employees to add');
      return;
    }
    try {
      await Promise.all(
        employeeSelection.map((employeeId) =>
          appointmentFlowApi.addServiceEmployee({
            serviceId: selectedServiceId,
            employeeId,
          })
        )
      );
      setEmployeeSelection([]);
      await loadServiceEmployees(selectedServiceId);
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add employees');
    }
  };

  const handleRemoveEmployee = async (employeeId) => {
    const isConfirmed = await confirm({
      title: 'Unassign Employee',
      message: 'Unassign this employee?',
      confirmText: 'Unassign',
      confirmColor: 'warning',
      severity: 'warning',
    });
    if (!isConfirmed) return;
    try {
      await appointmentFlowApi.removeServiceEmployee({
        serviceId: selectedServiceId,
        employeeId,
      });
      setServiceEmployees((prev) => prev.filter((employee) => employee.id !== employeeId));
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to unassign employee');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const handleOpenActions = (event, branch) => {
    setActionAnchorEl(event.currentTarget);
    setActionBranch(branch);
  };

  const handleCloseActions = () => {
    setActionAnchorEl(null);
    setActionBranch(null);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const workingDaysLabel = useMemo(
    () =>
      new Map(
        DAYS.map((day) => [day.value, day.label])
      ),
    []
  );

  const formatWorkingDays = (days) => {
    if (!Array.isArray(days) || !days.length) return '-';
    return days
      .map((day) => workingDaysLabel.get(day))
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Button variant="text" startIcon={<ArrowLeft />} href="/appointments" sx={{ minWidth: 0, px: 0 }}>
          Back
        </Button>
        <Box>
          <Typography variant="h5">Branches Management</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage branches, services, and employee assignments in one place.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <Search size={16} />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search branches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Stack>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
            >
              Add New Branch
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Branch Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Working Days</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Open</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Close</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No branches found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((branch, index) => (
                    <TableRow key={branch.id} hover>
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell>{branch.name || '-'}</TableCell>
                      <TableCell>{formatWorkingDays(branch.workingDays)}</TableCell>
                      <TableCell>{branch.openTime || '-'}</TableCell>
                      <TableCell>{branch.closeTime || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel(branch.status)}
                          size="small"
                          sx={{
                            bgcolor: statusLabel(branch.status) === 'active' ? 'success.light' : 'error.light',
                            color: statusLabel(branch.status) === 'active' ? 'success.contrastText' : 'error.contrastText',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(event) => handleOpenActions(event, branch)}
                          startIcon={<MoreVertical size={14} />}
                          sx={{ textTransform: 'none' }}
                        >
                          Actions
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Add New Branch</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Branch Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Working Days
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {DAYS.map((day) => (
                  <Stack key={day.value} direction="row" spacing={0.5} alignItems="center">
                    <Checkbox
                      size="small"
                      checked={formData.workingDays.includes(day.value)}
                      onChange={() => handleWorkingDayToggle(day.value)}
                    />
                    <Typography variant="body2">{day.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Open Time"
                name="openTime"
                type="time"
                fullWidth
                value={formData.openTime}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Close Time"
                name="closeTime"
                type="time"
                fullWidth
                value={formData.closeTime}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Timezone"
                name="timezone"
                fullWidth
                value={formData.timezone}
                onChange={handleInputChange}
                placeholder="e.g., Asia/Kolkata"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit}>
            Add Branch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Branch</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Branch Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Working Days
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {DAYS.map((day) => (
                  <Stack key={day.value} direction="row" spacing={0.5} alignItems="center">
                    <Checkbox
                      size="small"
                      checked={formData.workingDays.includes(day.value)}
                      onChange={() => handleWorkingDayToggle(day.value)}
                    />
                    <Typography variant="body2">{day.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Open Time"
                name="openTime"
                type="time"
                fullWidth
                value={formData.openTime}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Close Time"
                name="closeTime"
                type="time"
                fullWidth
                value={formData.closeTime}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Timezone"
                name="timezone"
                fullWidth
                value={formData.timezone}
                onChange={handleInputChange}
                placeholder="e.g., Asia/Kolkata"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Update Branch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showServicesModal}
        onClose={() => setShowServicesModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Services</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selectedBranch?.name || 'Branch'}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => setShowServicesModal(false)}>
              Close
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Add Services
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search services..."
                      value={serviceSearch}
                      onChange={(event) => setServiceSearch(event.target.value)}
                    />
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Services</InputLabel>
                      <Select
                        multiple
                        label="Services"
                        value={serviceSelection}
                        onChange={(event) => {
                          const value = event.target.value;
                          setServiceSelection(typeof value === 'string' ? value.split(',') : value);
                        }}
                        renderValue={(selected) =>
                          selected
                            .map((id) => allServices.find((service) => service.id === id)?.name)
                            .filter(Boolean)
                            .join(', ')
                        }
                      >
                        {selectableServices
                          .filter((service) =>
                            service.name.toLowerCase().includes(serviceSearch.toLowerCase())
                          )
                          .map((service) => (
                            <MenuItem key={service.id} value={service.id}>
                              <Checkbox checked={serviceSelection.includes(service.id)} />
                              {service.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button variant="outlined" onClick={() => setServiceSelection([])}>
                        Clear
                      </Button>
                      <Button variant="contained" onClick={handleAddSelectedServices}>
                        Add Selected
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Service Employees
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Service</InputLabel>
                      <Select
                        label="Select Service"
                        value={selectedServiceId}
                        onChange={(event) => {
                          const nextServiceId = event.target.value;
                          setSelectedServiceId(nextServiceId);
                          setEmployeeSelection([]);
                          setEmployeeSearch('');
                          loadAllEmployees();
                          loadServiceEmployees(nextServiceId);
                        }}
                      >
                        {branchServices.map((service) => (
                          <MenuItem key={service.id} value={service.id}>
                            {service.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search employees..."
                      value={employeeSearch}
                      onChange={(event) => setEmployeeSearch(event.target.value)}
                      sx={{ mt: 2 }}
                    />
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Employees</InputLabel>
                      <Select
                        multiple
                        label="Employees"
                        value={employeeSelection}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEmployeeSelection(typeof value === 'string' ? value.split(',') : value);
                        }}
                        renderValue={(selected) =>
                          selected
                            .map((id) => allEmployees.find((employee) => employee.id === id)?.name)
                            .filter(Boolean)
                            .join(', ')
                        }
                      >
                        {selectableEmployees
                          .filter((employee) =>
                            employee.name.toLowerCase().includes(employeeSearch.toLowerCase())
                          )
                          .map((employee) => (
                            <MenuItem key={employee.id} value={employee.id}>
                              <Checkbox checked={employeeSelection.includes(employee.id)} />
                              {employee.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button variant="outlined" onClick={() => setEmployeeSelection([])}>
                        Clear
                      </Button>
                      <Button variant="contained" onClick={handleAddEmployees}>
                        Add Employees
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1">Mapped Services</Typography>
                      <Chip
                        label={`${branchServices.length}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(37, 99, 235, 0.08)', color: 'primary.main' }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      {branchServices.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No services mapped yet.
                        </Typography>
                      ) : (
                        branchServices.map((service) => (
                          <Chip
                            key={service.id}
                            label={service.name}
                            onDelete={() => handleRemoveService(service.id)}
                            sx={{ mb: 1 }}
                          />
                        ))
                      )}
                    </Stack>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {branchServices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                No services found
                              </TableCell>
                            </TableRow>
                          ) : (
                            branchServices.map((service) => (
                              <TableRow key={service.id}>
                                <TableCell>{service.name || '-'}</TableCell>
                                <TableCell>{service.durationMinutes || '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={statusLabel(service.status)}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        statusLabel(service.status) === 'active'
                                          ? 'success.light'
                                          : 'error.light',
                                      color:
                                        statusLabel(service.status) === 'active'
                                          ? 'success.contrastText'
                                          : 'error.contrastText',
                                      textTransform: 'capitalize',
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<Trash2 size={14} />}
                                    onClick={() => handleRemoveService(service.id)}
                                  >
                                    Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1">Service Employees</Typography>
                      <Chip
                        label={`${serviceEmployees.length}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)', color: 'success.main' }}
                      />
                    </Stack>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {serviceEmployees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                {selectedServiceId ? 'No employees assigned' : 'Select a service'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            serviceEmployees.map((employee) => (
                              <TableRow key={employee.id}>
                                <TableCell>{employee.name || '-'}</TableCell>
                                <TableCell>{employee.email || '-'}</TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    onClick={() => handleRemoveEmployee(employee.id)}
                                  >
                                    Unassign
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

          </Stack>
        </DialogContent>
        <DialogActions sx={{ display: 'none' }} />
      </Dialog>

      <Dialog
        open={showEmployeesModal}
        onClose={() => setShowEmployeesModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Assign Employees</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selectedBranch?.name || 'Branch'}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => setShowEmployeesModal(false)}>
              Close
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Select Service
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Service</InputLabel>
                          <Select
                            label="Service"
                            value={selectedServiceId}
                            onChange={(event) => {
                              const nextServiceId = event.target.value;
                              setSelectedServiceId(nextServiceId);
                              setEmployeeSelection([]);
                              setEmployeeSearch('');
                              loadServiceEmployees(nextServiceId);
                            }}
                          >
                            {branchServices.map((service) => (
                              <MenuItem key={service.id} value={service.id}>
                                {service.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search employees..."
                          value={employeeSearch}
                          onChange={(event) => setEmployeeSearch(event.target.value)}
                          sx={{ mt: 2 }}
                        />
                        <FormControl fullWidth sx={{ mt: 2 }}>
                          <InputLabel>Employees</InputLabel>
                          <Select
                            multiple
                            label="Employees"
                            value={employeeSelection}
                            onChange={(event) => {
                              const value = event.target.value;
                              setEmployeeSelection(typeof value === 'string' ? value.split(',') : value);
                            }}
                            renderValue={(selected) =>
                              selected
                                .map((id) => allEmployees.find((employee) => employee.id === id)?.name)
                                .filter(Boolean)
                                .join(', ')
                            }
                          >
                            {selectableEmployees
                              .filter((employee) =>
                                employee.name.toLowerCase().includes(employeeSearch.toLowerCase())
                              )
                              .map((employee) => (
                                <MenuItem key={employee.id} value={employee.id}>
                                  <Checkbox checked={employeeSelection.includes(employee.id)} />
                                  {employee.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                          <Button variant="outlined" onClick={() => setEmployeeSelection([])}>
                            Clear
                          </Button>
                          <Button variant="contained" onClick={handleAddEmployees}>
                            Assign Selected
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1">Assigned Employees</Typography>
                          <Chip
                            label={`${serviceEmployees.length}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)', color: 'success.main' }}
                          />
                        </Stack>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {serviceEmployees.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    {selectedServiceId ? 'No employees assigned' : 'Select a service'}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                serviceEmployees.map((employee) => (
                                  <TableRow key={employee.id}>
                                    <TableCell>{employee.name || '-'}</TableCell>
                                    <TableCell>{employee.email || '-'}</TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={() => handleRemoveEmployee(employee.id)}
                                      >
                                        Unassign
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ display: 'none' }} />
      </Dialog>

      <Menu
        anchorEl={actionAnchorEl}
        open={isActionMenuOpen}
        onClose={handleCloseActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleOpenServices(actionBranch);
            handleCloseActions();
          }}
        >
          Services
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenEmployees(actionBranch);
            handleCloseActions();
          }}
        >
          Assign Employees
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleEdit(actionBranch);
            handleCloseActions();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDelete(actionBranch.id);
            handleCloseActions();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleStatusToggle(actionBranch.id, actionBranch.status);
            handleCloseActions();
          }}
        >
          {statusLabel(actionBranch?.status) === 'active' ? 'Set Inactive' : 'Set Active'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BranchesPage;
